import { agentPromptDisplay } from "../../shared/model/agentPromptDisplay";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Tool } from "@ag-ui/client";
import { generateUuid } from "@/lib/uuid";
import type { ProposalDecision, ProposalVersion } from "@/features/catalog_case_review/shared/model/agentProposal";
import type { FieldActionContext } from "@/features/catalog_case_review/shared/model/fieldActionContext";
import { useAgentThread } from "../../shared/context/AgentThreadContext";
import { useAgentCapabilities, useWorkingFieldPublisher } from "../../shared/context/AgentCapabilityContext";
import { agentThreadService, isRunInProgress } from "../../shared/services/agentThreadService";
import { entriesFor, fieldActionEntry } from "../../shared/utils/aguiContextEntries";
import { describeRunFailure } from "../../shared/utils/runFailure";
import { DOCK_LABELS, toolLabel } from "../../shared/constants/pageAgentLabels";
import { ABORT_CODE, MAX_AUTO_CONTINUE_RUNS, RUN_CANCELLED_CODE } from "../../shared/constants/pageAgentIds";
import type { AgentStep, AgentToolCall, AgentTurn, ProposalCardRow } from "../../shared/model/agentTurn";
import { isProposal } from "../../frontendTools/proposeFieldValue";
import { useAgentFrontendTools } from "./useAgentFrontendTools";

export interface AgentConversation {
  turns: AgentTurn[];
  toolCalls: AgentToolCall[];
  steps: AgentStep[];
  /** The values Theragenie proposed during the last question, from the tool results alone. */
  proposals: ProposalCardRow[];
  /** While a question runs, what Theragenie is doing right now ("Looking up MedDRA terms"); null when idle. */
  workingLabel: string | null;
  /** Changes whenever the conversation grows (a turn, text, a step, a card), so a panel can follow it. */
  contentVersion: string;
  /** What became of each proposal, by key, as the Case Review reports it. */
  proposalDecisions: Record<string, ProposalDecision>;
  /** Where each pending proposal stands in its history, by key, as the Case Review reports it. */
  proposalVersions: Record<string, ProposalVersion>;
  /**
   * The card rows whose proposal the form has let go of (Save, Cancel or a reload): a key the
   * decisions once held and no longer do. Kept here, not in the card, so closing the dock keeps them.
   */
  closedProposalKeys: ReadonlySet<string>;
  question: string;
  setQuestion: (text: string) => void;
  /** The field action pinned for the next question, as its chip reads; null when none is. */
  fieldActionChip: string | null;
  /** Pins a field action to the next question: its context rides on every run of that turn. */
  beginFieldAction: (context: FieldActionContext, actionLabel: string) => void;
  /** Drops the pinned field action: the next question is an ordinary one. */
  clearFieldAction: () => void;
  /** True while a run is in flight on the thread, whichever surface started it. */
  busy: boolean;
  /** True while the stored conversation is being fetched. */
  loading: boolean;
  /** Why the stored conversation could not be loaded, or null. */
  threadError: string | null;
  /** The last run's failure, or null. Cleared when the next run starts. */
  runError: string | null;
  /** True once a run answered that the agent is not configured here. */
  unavailable: boolean;
  /** Sends the given text, or the composer's question when none is given. */
  ask: (text?: string) => Promise<void>;
  stop: () => Promise<void>;
  /** True when "Clear chat" can act: something to clear, no run in flight, no clear already going. */
  canClear: boolean;
  /** True while the server is clearing the conversation. */
  clearing: boolean;
  /** Why the last clear failed, or null. The transcript is kept when it does. */
  clearError: string | null;
  /**
   * Clears the conversation on the server, then here: transcript, trail, proposal cards and their
   * closed-memory, the field-action chip and the run error. Keeps the thread id, and leaves the
   * form's own pending proposals alone. Resolves whether it cleared.
   */
  clearConversation: () => Promise<boolean>;
  /** The card's ✓ / ✕ / Undo / Redo / "Go to field": the same handlers the form's row uses. */
  acceptProposal: (key: string) => void;
  rejectProposal: (key: string) => void;
  undoProposal: (key: string) => void;
  redoProposal: (key: string) => void;
  goToProposal: (row: ProposalCardRow) => void;
}

/**
 * One question, in as many runs as it takes: `agent.addMessage` (the list is instance state on the
 * agent; without it the server answers 400), then `agent.runAgent` with the run id minted here so
 * `stop` can address it, the screen's location as first-class `context`, and the screen's frontend
 * tool declarations. A frontend call the run makes is answered here on `TOOL_CALL_END`, its result
 * appended as a tool message, and — because the server ends the run on such a call — the next run
 * starts by itself once every call of the run has been answered.
 */
export function useAgentConversation(): AgentConversation {
  const { threadId, agent, loading, error: threadError, turns, setTurns, forgetTranscript, transcriptGeneration,
    run: { busy, setBusy, activeRunId, stopping } } = useAgentThread();
  const tools = useAgentFrontendTools();
  const { snapshot } = useAgentCapabilities();
  const publishWorkingField = useWorkingFieldPublisher();
  const [toolCalls, setToolCalls] = useState<AgentToolCall[]>([]);
  const [steps, setSteps] = useState<AgentStep[]>([]);
  const [proposals, setProposals] = useState<ProposalCardRow[]>([]);
  const [question, setQuestion] = useState("");
  const [runError, setRunError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  const proposalCount = useRef(0);
  // Every card key the decisions have held during this question: a key they drop afterwards is closed.
  const seenProposalKeys = useRef(new Set<string>());
  // The pinned field action: a ref so an immediate send sees it in the same tick, and state for the chip.
  const pendingFieldAction = useRef<FieldActionContext | null>(null);
  const [fieldActionChip, setFieldActionChip] = useState<string | null>(null);
  const beginFieldAction = useCallback((context: FieldActionContext, actionLabel: string) => {
    pendingFieldAction.current = context;
    setFieldActionChip(DOCK_LABELS.fieldActionChip(actionLabel, context.fieldLabel));
  }, []);
  const clearFieldAction = useCallback(() => {
    pendingFieldAction.current = null;
    setFieldActionChip(null);
  }, []);

  // A forgotten transcript takes this surface's per-question state with it, whichever surface
  // cleared it (each surface holds its own). Skips the mount: nothing was cleared.
  const seenGeneration = useRef(transcriptGeneration);
  useEffect(() => {
    if (seenGeneration.current === transcriptGeneration) return;
    seenGeneration.current = transcriptGeneration;
    setToolCalls([]);
    setSteps([]);
    setProposals([]);
    seenProposalKeys.current = new Set();
    proposalCount.current = 0;
    setRunError(null);
    setClearError(null);
    clearFieldAction();
  }, [transcriptGeneration, clearFieldAction]);

  const appendDelta = useCallback((messageId: string, delta: string) => {
    setTurns((current) => {
      const index = current.findIndex((turn) => turn.id === messageId);
      if (index === -1) return [...current, { id: messageId, role: "assistant", text: delta, notice: null }];
      const next = [...current];
      next[index] = { ...next[index], text: next[index].text + delta };
      return next;
    });
  }, [setTurns]);

  /** Writes a notice on the last turn, once: a turn that already carries one keeps it. */
  const noteOnLastTurn = useCallback((notice: string) => {
    setTurns((current) => {
      if (current.length === 0 || current[current.length - 1].notice !== null) return current;
      const next = [...current];
      next[next.length - 1] = { ...next[next.length - 1], notice };
      return next;
    });
  }, [setTurns]);

  const finishCall = (id: string, patch: Partial<AgentToolCall>) =>
    setToolCalls((current) => current.map((call) => (call.id === id ? { ...call, ...patch, finished: true } : call)));

  /** Answers one frontend call: executes it, writes its result on the trail and into the thread. */
  const answerFrontendCall = async (runId: string, toolCallId: string, name: string, rawArgs: string, fieldAction: FieldActionContext | null): Promise<void> => {
    const result = await tools.execute(name, rawArgs, { runId, toolCallId, fieldAction });
    const content = JSON.stringify(result);
    finishCall(toolCallId, { result: content });
    agent.addMessage({ id: generateUuid(), role: "tool", toolCallId, content });
    if (!isProposal(result)) return;
    const row: ProposalCardRow = { key: result.key, recordId: result.recordId, changes: result.changes, rationale: result.rationale };
    setProposals((current) => [...current, row]);
    // The first proposal of a question takes the reader to it; the rest are one card away.
    if (proposalCount.current === 0) tools.caseReview()?.goToField(row.changes[0].fieldId, row.recordId);
    proposalCount.current += 1;
  };

  /**
   * One run. Frontend calls are collected while the stream is applied and answered only after it
   * has been: `addMessage` mid-stream would race the client's own message updates. Resolves with
   * how many calls were answered (the server has not seen them) and whether the run ended in error.
   */
  const runOnce = async (fieldAction: FieldActionContext | null): Promise<{ answered: number; failed: boolean }> => {
    const runId = generateUuid();
    activeRunId.current = runId;
    const declared: Tool[] = tools.declarations();
    const names = new Map<string, string>();
    const args = new Map<string, string>();
    const calls: Array<{ toolCallId: string; name: string }> = [];
    let failed = false;
    const context = [...entriesFor(tools.location()), ...(fieldAction === null ? [] : [fieldActionEntry(fieldAction)])];
    await agent.runAgent({ runId, context, tools: declared }, {
      onTextMessageContentEvent: ({ event }) => appendDelta(event.messageId, event.delta),
      onStepStartedEvent: ({ event }) => setSteps((current) => [...current, { name: event.stepName, finished: false }]),
      onStepFinishedEvent: ({ event }) => setSteps((current) =>
        current.map((step) => (step.name === event.stepName ? { ...step, finished: true } : step))),
      onToolCallStartEvent: ({ event }) => {
        names.set(event.toolCallId, event.toolCallName);
        setToolCalls((current) => [...current,
          { id: event.toolCallId, label: toolLabel(event.toolCallName), args: "", result: null, finished: false }]);
      },
      onToolCallArgsEvent: ({ event }) => {
        args.set(event.toolCallId, (args.get(event.toolCallId) ?? "") + event.delta);
        setToolCalls((current) => current.map((call) => (call.id === event.toolCallId ? { ...call, args: call.args + event.delta } : call)));
      },
      onToolCallEndEvent: ({ event }) => {
        finishCall(event.toolCallId, {});
        const name = names.get(event.toolCallId);
        // Only a tool this run declared runs here; a server tool's result arrives in the stream.
        if (name === undefined || !tools.isDeclared(name, declared)) return;
        calls.push({ toolCallId: event.toolCallId, name });
      },
      onToolCallResultEvent: ({ event }) => finishCall(event.toolCallId, { result: event.content }),
      onRunErrorEvent: ({ event }) => {
        // The client does not reject on a browser abort: mid-stream it emits RUN_ERROR with its own
        // `abort` code, and before headers it resolves silently. Both are a Stop, not a failure.
        const cancelled = event.code === RUN_CANCELLED_CODE || (stopping.current && event.code === ABORT_CODE);
        if (cancelled) noteOnLastTurn(DOCK_LABELS.cancelledNote);
        else setRunError(event.message);
        failed = true;
      },
    });
    // The run is over: a Stop from here on has nothing to cancel on the server.
    activeRunId.current = null;
    for (const call of calls) await answerFrontendCall(runId, call.toolCallId, call.name, args.get(call.toolCallId) ?? "", fieldAction);
    return { answered: calls.length, failed };
  };
  // `ask` is handed to the field-action composer, which must not change identity with every render
  // (its identity reaches provider state); the run itself reads the latest closure through this ref.
  const latestRunOnce = useRef(runOnce);
  latestRunOnce.current = runOnce;

  const ask = useCallback(async (text?: string): Promise<void> => {
    const asked = (text ?? question).trim();
    if (asked === "" || busy) return;
    // The pinned field action belongs to this turn alone: every run of it carries the entry, the next question none.
    const fieldAction = pendingFieldAction.current;
    pendingFieldAction.current = null;
    setFieldActionChip(null);
    stopping.current = false;
    proposalCount.current = 0;
    setBusy(true);
    setRunError(null);
    setClearError(null);
    setQuestion("");
    setToolCalls([]);
    setSteps([]);
    setProposals([]);
    seenProposalKeys.current = new Set();
    // The field whose button started this question shows that Theragenie is working on it.
    publishWorkingField(fieldAction === null ? null : { fieldId: fieldAction.fieldId, recordId: fieldAction.recordId ?? null });
    const userMessageId = generateUuid();
    setTurns((current) => [...current, { id: userMessageId, role: "user", text: agentPromptDisplay(asked), notice: null }]);
    agent.addMessage({ id: userMessageId, role: "user", content: asked });
    try {
      // The server ends a run on a frontend call; the next run carries the answer. Bounded, so a
      // model that keeps calling tools cannot keep the browser running forever. A run that ended
      // in error (or was stopped) is not continued: its calls are not pending on the server.
      let run = 0;
      for (; run < MAX_AUTO_CONTINUE_RUNS; run += 1) {
        const { answered, failed } = await latestRunOnce.current(fieldAction);
        if (answered === 0 || failed || stopping.current) break;
      }
      if (run === MAX_AUTO_CONTINUE_RUNS) noteOnLastTurn(DOCK_LABELS.runCapNote);
    } catch (err: unknown) {
      const failure = describeRunFailure(err);
      setUnavailable(failure.unavailable);
      setRunError(failure.message);
    } finally {
      activeRunId.current = null;
      publishWorkingField(null);
      setBusy(false);
    }
  }, [agent, activeRunId, busy, noteOnLastTurn, publishWorkingField, question, setBusy, setTurns, stopping]);

  /**
   * Cancels on the server, then aborts the stream on this same agent object. The note is written here
   * as well, for a run aborted before any frame arrived (the client then resolves without an event).
   */
  const stop = async (): Promise<void> => {
    stopping.current = true;
    const runId = activeRunId.current;
    // Between two runs of one question there is nothing in flight; the flag alone ends the loop.
    if (runId === null) return;
    try {
      await agentThreadService.cancelRun(runId);
    } finally {
      agent.abortRun();
      noteOnLastTurn(DOCK_LABELS.cancelledNote);
    }
  };

  const canClear = !busy && !clearing && turns.length > 0;

  const clearConversation = useCallback(async (): Promise<boolean> => {
    if (!canClear) return false;
    setClearing(true);
    setClearError(null);
    try {
      await agentThreadService.clearMessages(threadId);
      forgetTranscript();
      return true;
    } catch (err: unknown) {
      setClearError(isRunInProgress(err) ? DOCK_LABELS.clearRunInProgress : DOCK_LABELS.clearFailed);
      return false;
    } finally {
      setClearing(false);
    }
  }, [canClear, threadId, forgetTranscript]);

  const closedProposalKeys = closedKeys(proposals, snapshot.proposalDecisions, seenProposalKeys.current);
  const workingLabel = busy ? workingLabelFor(toolCalls) : null;
  const lastTurn = turns[turns.length - 1];
  const contentVersion = [turns.length, lastTurn?.text.length ?? 0, toolCalls.length,
    toolCalls.filter((call) => call.finished).length, proposals.length, workingLabel ?? ""].join("|");

  return {
    turns, toolCalls, steps, proposals, workingLabel, contentVersion, proposalDecisions: snapshot.proposalDecisions, proposalVersions: snapshot.proposalVersions,
    closedProposalKeys,
    question: agentPromptDisplay(question), setQuestion, fieldActionChip, beginFieldAction, clearFieldAction,
    busy, loading, threadError, runError, unavailable, ask, stop,
    canClear, clearing, clearError, clearConversation,
    acceptProposal: (key) => tools.caseReview()?.proposals.accept(key),
    rejectProposal: (key) => tools.caseReview()?.proposals.reject(key),
    undoProposal: (key) => tools.caseReview()?.proposals.undo(key),
    redoProposal: (key) => tools.caseReview()?.proposals.redo(key),
    goToProposal: (row) => tools.caseReview()?.goToField(row.changes[0].fieldId, row.recordId),
  };
}

/**
 * The rows whose key the decisions once held and no longer do. A key the decisions have never held
 * is a row that has just arrived (the snapshot is published a render after the tool result), and
 * stays open. Records every key the decisions hold in `seen`.
 */
function closedKeys(rows: ProposalCardRow[], decisions: Record<string, ProposalDecision>, seen: Set<string>): ReadonlySet<string> {
  const closed = new Set<string>();
  for (const { key } of rows) {
    if (key in decisions) seen.add(key);
    else if (seen.has(key)) closed.add(key);
  }
  return closed;
}

/**
 * What the working indicator says while a question runs: the tool call in progress ("Looking up
 * MedDRA terms"), or, between calls and while the model writes, that it is thinking.
 */
function workingLabelFor(calls: AgentToolCall[]): string {
  const running = [...calls].reverse().find((call) => !call.finished);
  return running === undefined ? DOCK_LABELS.workingThinking : running.label;
}
